/**
 * Phase 26 / Task 26.4 (DEC-147 #9) — server-side uppercase-on-write normalizer.
 *
 * Enforce UPPERCASE at the API validation boundary for hauler-entered IDENTIFIER
 * fields: location names, tank names/numbers, private operator/customer names,
 * addresses, seal numbers. The root CLAUDE.md "Conventions" entry makes this a
 * project-wide, non-negotiable rule — enforced server-side regardless of client.
 *
 * FREE-TEXT NOTES/COMMENTS ARE EXEMPT — driver notes, dispatcher notes, InReview
 * comments, audit notes, invoice notes are prose (not identifiers) and must be
 * preserved verbatim. Never wrap those fields with this helper.
 *
 * Locale note: default `.toUpperCase()` uses the runtime's implicit locale, which
 * is the classic Turkish-dotless-i pitfall (i → İ instead of I). lodeX
 * identifiers are ASCII/US-English (oilfield abbreviations, seal numbers,
 * addresses) so we pin `en-US` explicitly for deterministic output across every
 * server + CI + test env.
 */

import { z } from 'zod';

/**
 * Pure normalizer used by the Zod builder below AND by any non-Zod caller that
 * needs the same transformation (e.g. hand-normalizing an inbound value before
 * comparing against a stored uppercased row). Trims first, then locale-safe
 * uppercases.
 */
export function toUpperIdentifier(v: string): string {
  return v.trim().toLocaleUpperCase('en-US');
}

/**
 * Zod builder for the common identifier shape. Trims, applies optional min/max
 * on the trimmed input, then upper-cases the output. Compose with `.optional()`
 * / `.nullable()` at the call site.
 *
 * Example:
 *   const createBody = z.object({
 *     name: upperCaseIdentifier({ min: 1, max: 160 }),
 *     tankNumber: upperCaseIdentifier({ min: 1, max: 40 }).optional(),
 *   });
 *
 * Callers that need extra validation (e.g. an abbreviation regex) can compose
 * around this helper — the transform is chained last so any refinements a
 * caller adds still see the uppercased output.
 */
export function upperCaseIdentifier(
  constraints: { min?: number; max?: number } = {},
): z.ZodEffects<z.ZodString, string, string> {
  let schema = z.string().trim();
  if (constraints.min !== undefined) schema = schema.min(constraints.min);
  if (constraints.max !== undefined) schema = schema.max(constraints.max);
  return schema.transform(toUpperIdentifier);
}
